import { animate, m, useMotionValue, useTransform } from 'motion/react';
import { useEffect, useRef } from 'react';

const progressRange = [0, 5, 95, 100];
const opacityRange = [0, 1, 1, 0];

const getPointAtLength = (
  path: SVGPathElement | null,
  progress: number,
  fallback: { x: number; y: number },
  direction: 'forwards' | 'backwards' = 'forwards',
) => {
  if (!path) return fallback;
  const percent = direction === 'forwards' ? progress / 100 : 1 - progress / 100;
  const point = path.getPointAtLength(path.getTotalLength() * percent);
  return { x: point.x, y: point.y };
};

const usePathAnimation = (
  pathRef: React.RefObject<SVGPathElement | null>,
  direction: 'forwards' | 'backwards' = 'forwards',
) => {
  const progress = useMotionValue(0);
  const opacity = useTransform(progress, progressRange, opacityRange);
  const point = useTransform(progress, (latest) =>
    getPointAtLength(pathRef.current, latest, { x: 0, y: 0 }, direction),
  );
  const x = useTransform(point, (point) => point.x);
  const y = useTransform(point, (point) => point.y);
  return { progress, opacity, x, y };
};

export const Animation = (props: { className?: string }) => {
  const cubePathRef = useRef<SVGPathElement>(null);
  const cube = usePathAnimation(cubePathRef, 'backwards');

  const outerPathRef = useRef<SVGPathElement>(null);
  const outer = usePathAnimation(outerPathRef, 'forwards');

  const innerPathRef = useRef<SVGPathElement>(null);
  const inner = usePathAnimation(innerPathRef, 'backwards');

  useEffect(() => {
    const controls = animate(
      [
        [inner.progress, 100, { duration: 3 }],
        [outer.progress, 100, { duration: 4, at: 0.5 }],
        [cube.progress, 100, { duration: 2, at: 4 }],
      ],
      { repeat: Infinity, defaultTransition: { ease: 'easeInOut' } },
    );

    return () => controls.cancel();
  }, [cube.progress, outer.progress, inner.progress]);

  return (
    <m.svg
      width="347"
      height="508"
      viewBox="0 -20 347 508"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
      initial={{ translateY: 5 }}
      animate={{ translateY: -5 }}
      transition={{
        duration: 4,
        ease: 'easeInOut',
        repeat: Infinity,
        repeatType: 'reverse',
      }}
    >
      {/* Cube */}
      <g>
        <path
          d="M153.5 387.5V475L235 459.136V377.169L153.5 387.5Z"
          fill="#12406C"
          fillOpacity="0.43"
        />
        <path
          d="M109 371V450.763L153.5 475M153.5 475V387.5L235 377.169V459.136L153.5 475Z"
          stroke="#2D4467"
          strokeWidth="2"
        />
        <path
          opacity="0.6"
          d="M109.5 451V371L153 387.5V475L109.5 451Z"
          fill="url(#paint0_linear_20282_10102)"
        />
        <path
          d="M153 387L108.5 370.5L183.5 363L235.5 377.5L153 387Z"
          fill="#0D1C36"
          fillOpacity="0.11"
          stroke="#2D4467"
          strokeWidth="3"
        />
      </g>
      {/* Glowing stroke */}
      <m.path
        d="M109 371V450.763L153.5 475M153.5 475V387.5L235 377.169V459.136L153.5 475Z"
        stroke="url(#glowing_stroke_cube)"
        strokeWidth="3"
        opacity={cube.opacity}
      />
      {/* Invisible path for the animation */}
      <path ref={cubePathRef} d="M108.5 451L153.5 475L235 459V378" />
      {/* Cube glowing circle */}
      <g>
        <m.circle cx={cube.x} cy={cube.y} fill="#F3FBFF" r="2.5" opacity={cube.opacity} />
        <g filter="url(#blur_md)">
          <m.circle cx={cube.x} cy={cube.y} r="8" fill="#F3FBFF" opacity={cube.opacity} />
        </g>
        <g filter="url(#blur_sm)">
          <m.circle cx={cube.x} cy={cube.y} r="4.66667" fill="#F3FBFF" opacity={cube.opacity} />
        </g>
      </g>
      {/* Question mark */}
      <g>
        <path
          d="M1.29564 168.5L35.1268 158.5C31.5274 72.1 65.2865 24 109.295 9C24.7865 27.5 -2.19931 100 1.29564 168.5Z"
          fill="url(#paint2_linear_20282_10102)"
        />
        <path
          d="M175.295 193.5C215.295 118.5 161.295 101.167 136.295 105C140.295 90.5 160.495 66.0531 201.295 84.8531C252.295 108.353 237.795 152 229.295 172.5C225.295 185.5 174.295 241.5 157.295 267.853C153.795 300 156.295 340.5 156.295 340.5L112.295 330C112.795 312 113.197 274.976 116.294 267.853C125.577 246.5 132.296 256.147 175.295 193.5Z"
          fill="url(#paint3_linear_20282_10102)"
          fillOpacity="0.38"
        />
        <path
          d="M34.7949 158L1.29492 169L86.7949 173L129.795 164L34.7949 158Z"
          fill="url(#paint4_linear_20282_10102)"
          stroke="#2D4568"
          strokeWidth="2"
        />
        <path
          d="M156.295 339.5L112.295 330C111.629 312.333 113.929 280.437 114.795 274.5C116.836 260.5 128.795 251.276 161.295 212.776C176.295 194.276 202.139 148.569 182.295 124C161.295 97.9995 139.295 105 135.295 106.5"
          stroke="#2D4467"
          strokeWidth="3"
        />
        <path
          d="M166.308 2.42223C47.1084 -5.57777 29.3084 102.089 35.3084 156.922L129.309 163.422C121.309 104.422 148.309 60.9224 203.309 84.9224C255.188 107.561 234.529 163.031 230.309 172.422C223.309 188 196.809 218.922 176.809 241.422C156.809 263.922 158.309 267.922 156.809 274.422C155.599 279.666 155.975 320 156.809 339.5L232.809 333.5C232.809 325 231.484 316 236.309 292.5C241.133 269 295.795 254.5 312.295 199C357.168 48.0623 227.642 4.75561 166.308 2.42223Z"
          fill="url(#paint5_linear_20282_10102)"
          stroke="#2D4467"
          strokeWidth="2"
        />
      </g>
      {/* Outer glowing stroke */}
      <m.path
        d="M166.308 2.42223C47.1084 -5.57777 29.3084 102.089 35.3084 156.922L129.309 163.422C121.309 104.422 148.309 60.9224 203.309 84.9224C255.188 107.561 234.529 163.031 230.309 172.422C223.309 188 196.809 218.922 176.809 241.422C156.809 263.922 158.309 267.922 156.809 274.422C155.599 279.666 155.975 320 156.809 339.5L232.809 333.5C232.809 325 231.484 316 236.309 292.5C241.133 269 295.795 254.5 312.295 199C357.168 48.0623 227.642 4.75561 166.308 2.42223Z"
        stroke="url(#glowing_stroke_outer)"
        strokeWidth="3"
        opacity={outer.opacity}
      />
      {/* Invisible path for the animation */}
      <path
        ref={outerPathRef}
        d="M35.0135 156.922C29.0135 102.089 46.8135 -5.57777 166.013 2.42223C227.347 4.75561 356.873 48.0623 312 199C295.5 254.5 240.838 269 236.014 292.5C231.189 316 232.514 325 232.514 333.5"
      />

      {/* Outer glowing circle */}
      <g>
        <m.circle cx={outer.x} cy={outer.y} r="2.5" fill="#F3FBFF" opacity={outer.opacity} />
        <g filter="url(#blur_md)">
          <m.circle cx={outer.x} cy={outer.y} r="8" fill="#F3FBFF" opacity={outer.opacity} />
        </g>
        <g filter="url(#blur_sm)">
          <m.circle cx={outer.x} cy={outer.y} r="4.66667" fill="#F3FBFF" opacity={outer.opacity} />
        </g>
      </g>

      {/* Inner glowing stroke */}
      <m.path
        d="M156.119 339.981L112.12 330.481C111.453 312.815 113.754 280.919 114.619 274.981C116.66 260.981 128.619 251.757 161.119 213.258C176.119 194.757 201.963 149.05 182.119 124.481C161.119 98.481 139.119 105.481 135.119 106.981"
        stroke="url(#glowing_stroke_inner)"
        strokeWidth="3"
        opacity={inner.opacity}
      />
      {/* Invisible path for the animation */}
      <path
        ref={innerPathRef}
        d="M113.12 330.481C112.453 312.815 114.754 280.919 115.619 274.981C117.66 260.981 129.619 251.757 162.119 213.258C177.119 194.757 202.963 149.05 183.12 124.481C162.12 98.481 140.119 105.481 136.119 106.981"
      />

      {/* Inner glowing circle */}
      <g>
        <m.circle cx={inner.x} cy={inner.y} r="2.5" fill="#F3FBFF" opacity={inner.opacity} />
        <g filter="url(#blur_md)">
          <m.circle cx={inner.x} cy={inner.y} r="8" fill="#F3FBFF" opacity={inner.opacity} />
        </g>
        <g filter="url(#blur_sm)">
          <m.circle cx={inner.x} cy={inner.y} r="4.66667" fill="#F3FBFF" opacity={inner.opacity} />
        </g>
      </g>

      {/* Defs */}
      <defs>
        {/* Non animated filters */}
        <filter id="blur_sm" filterUnits="userSpaceOnUse">
          <feGaussianBlur stdDeviation="4" />
        </filter>
        <filter id="blur_md" filterUnits="userSpaceOnUse">
          <feGaussianBlur stdDeviation="9.77678" />
        </filter>

        {/* Fill gradients */}
        <linearGradient
          id="paint2_linear_20282_10102"
          x1="56.1146"
          y1="11"
          x2="27.625"
          y2="160.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#03153C" />
          <stop offset="0.360705" stopColor="#2A557E" />
          <stop offset="1" stopColor="#143656" />
        </linearGradient>
        <linearGradient
          id="paint0_linear_20282_10102"
          x1="131.25"
          y1="371"
          x2="131.25"
          y2="475"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#32608A" />
          <stop offset="1" stopColor="#1F4468" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_20282_10102"
          x1="179.295"
          y1="78"
          x2="193.295"
          y2="311"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.18" />
          <stop offset="0.30092" stopColor="#DDDDDD" />
          <stop offset="1" stopColor="white" stopOpacity="0.45" />
        </linearGradient>
        <linearGradient
          id="paint4_linear_20282_10102"
          x1="1.29492"
          y1="165.5"
          x2="129.795"
          y2="165.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#33445E" />
          <stop offset="1" stopColor="#48566E" />
        </linearGradient>
        <linearGradient
          id="paint5_linear_20282_10102"
          x1="306.795"
          y1="129"
          x2="4.29493"
          y2="69"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" stopOpacity="0.17" />
        </linearGradient>
        {/* Stroke gradients */}
        <m.radialGradient
          id="glowing_stroke_cube"
          cx={cube.x}
          cy={cube.y}
          r="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#9DB9E6" />
          <stop offset="1" stopColor="#2D4467" />
        </m.radialGradient>
        <m.radialGradient
          id="glowing_stroke_outer"
          cx={outer.x}
          cy={outer.y}
          r="150"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#9DB9E6" />
          <stop offset="1" stopColor="#2D4467" />
        </m.radialGradient>
        <m.radialGradient
          id="glowing_stroke_inner"
          cx={inner.x}
          cy={inner.y}
          r="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#9DB9E6" />
          <stop offset="1" stopColor="#2D4467" />
        </m.radialGradient>
      </defs>
    </m.svg>
  );
};
